import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import classnames from 'classnames';

import { Result, Events, Log } from 'core/infrastructure';
import { addLineBreaks, sendFocusToInitialElement } from './Utils';
import { createFocusTrap } from 'focus-trap';

// Este módulo exporta el componente Dialog para crear diálogos y 4
// funciones de apoyo en una clase Modal:
//
// showMessage: muestra un cuadro de mensaje
// showConfirmation: muestra un cuadro de confirmación Sí/No
// showCustom: muestra un diálogo que contiene el componente recibido
// ifConfirm: muestra una confirmación Sí/No y ejecuta la callback si
//            se pulsa sí
//
// Cuando se utiliza la función showCustom, el componente debe ser
// capaz de recibir a través de sus props un actions?: DialogActions<T>
// que contiene métodos para cerrar el diálogo con éxito, indicando
// un resultado de tipo T, o con error, indicando un error de tipo any


// Marca el último zIndex usado para mostrar diálogos, para permitir
// apilarlos con sus backdrops correspondientes
let zIndex = 10000;
let dialogCount = 0;

// Componente para crear diálogos. Los hijos de este componente serán
// lo que irá dentro del <div class='modal-content'> de Bootstrap.
// Se encarga de ocultar el mostrar el diálogo en el componentDidMount
// y ocultarlo en componentWillUnmount. Incluye componentes de apoyo
// para crear cabecera, cuerpo y pie:
// - Dialog.Header
// - Dialog.Body
// - Dialog.Footer

type DialogProps = React.PropsWithChildren<{ onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>, kind?: 'base' | 'large' | 'xlarge' | 'medium' }>;

export class _Dialog extends React.Component<DialogProps, EmptyState> {

	public ref = React.createRef<HTMLDivElement>();

	render() {
		const classNames = classnames({
			'modal-dialog': true,
			'modal-lg': this.props.kind === 'large',
			'modal-xl': this.props.kind === 'xlarge',
			'modal-medium': this.props.kind === 'medium'
		});
		const onKeyDown = this.props.onKeyDown && this.props.onKeyDown;

		return <div ref={this.ref} onKeyDown={onKeyDown} className='modal' style={{ zIndex: zIndex }} role='dialog' tabIndex={-1}>
			<div className={classNames}>
				<div className='modal-content'>
					{this.props.children}
				</div>
			</div>
		</div>;
	}
}

export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>((props, ref) => {

	const dialog = React.useRef<_Dialog>(null);

	React.useImperativeHandle(ref, () => dialog.current?.ref.current!);

	return <_Dialog {...props} ref={dialog} />;
});


// Componentes para "ayudar" a la creación del contenido del diálogo
// Los pueden usar otros componentes para mantener el estilo que quieran

export const DialogHeader = (props: { children?: React.ReactNode }) => <div className='modal-header'>{props.children}</div>;
export const DialogBody = (props: { children?: React.ReactNode, style?: React.CSSProperties }) => <div className='modal-body' {...props} />;
export const DialogFooter = (props: { children?: React.ReactNode }) => <div className='modal-footer'>{props.children}</div>;


// Clase que agrupa las acciones que se pueden realizar desde un diálogo.
// Todos diálogos deben recibir en su props un actions?: DialogActions<T>.
// Permite cerrar el diálogo con un resultado de tipo T, o cancelarlo
// con un error de tipo any.
export class DialogActions<T> {

	private closing: boolean;

	constructor(private resolve: (value: Result<T>) => void, private close: () => void) {
		this.closing = false;
	}

	closeWith(value: T) {

		if (this.closing)
			return;

		this.closing = true;

		this.resolve(Result.ok(value));
		this.close();
	}

	cancel() {

		if (this.closing)
			return;

		this.closing = true;

		this.resolve(Result.error());
		this.close();
	}
}


// Muestra un cuadro de diálogo con el componente recibido. El componente
// puede crearse usando Dialog, Dialog.Header, Dialog.Body y Dialog.Footer,
// o crearse directamente a mano con las clases css de bootstrap adecuadas

export type DialogComponentProps<TResult> = {
	actions?: DialogActions<TResult>;
}

export function showScreen<TResult>(ScreenComponent: Nullable<React.ReactElement<DialogComponentProps<TResult>>>) {
	return new Promise<Result<TResult>>((resolve, _reject) => {

		dialogCount++;

		// Necesito hueco entre diálogos, al menos dos (uno para el diálogo y otro para su backdrop) pero por si acaso, dejo más.
		zIndex += 20;

		const currentId = `dialog-${zIndex}`;
		$('#app').append(`<div id="${currentId}" style="z-index: ${zIndex}" class="screen"></div>`);

		// Creo la trampa del foco indicando un elemento de fallback por si algún diálogo
		// no tiene elementos focusables, así evito que la aplicación se rompa
		const focusTrap = createFocusTrap(`#${currentId}`, { fallbackFocus: `#${currentId}`, escapeDeactivates: false, allowOutsideClick: true, clickOutsideDeactivates: false });

		const mountBackdrop = () => {
			focusTrap.activate();

			$(`#${currentId}`).modal({
				show: true,
				keyboard: false,
				// Hacer que se cierre pinchando el backdrop da muchos problemas.
				// Muerto el perro, se acabó la rabia
				backdrop: 'static',

			});
			// Muevo el último backdrop justo detrás de mi (zIndex - 1)
			$('.modal-backdrop').last().css('z-index', zIndex - 1);
			$('.modal-backdrop').last().on('click', () => {
				// Si hay click en el backdrop, mandamos el foco al primer div con tabindex=-1 del diálogo, que es lo que
				// usa el lector de código de barras para permitir recibir eventos de teclado. Esto es necesario porque si no,
				// en pantallas como la de fichar, si pulsas en el backdrop luego no puedes leer tarjetas.
				const dialogEl = document.querySelector(`#${currentId} div[tabindex="-1"]`) as HTMLElement | null;
				dialogEl?.focus();
			});

			void Events.raise('pos-screen:opened');

			// OJO: No mando al primer elemento porque en la comandera con el keypad no es tan sencillo
			//      saber lo que tenemos que hacer. De momento dejo la gestión que ya hacía la comandera del foco.
			// sendFocusToInitialElement(document.getElementById(currentId)!);
			// Ya que no mando el foco a ningún sitio, al menos lo quito de donde estuviera para que no quede en un
			// elemento del screen anterior, porque el usuaria interactuar con ella con el teclado, por ejemplo, pulsando
			// espacio para volver a hacer click en un botón
			(document.activeElement as HTMLElement | null)?.blur?.();
		};

		const root = ReactDOMClient.createRoot(document.getElementById(currentId)!);

		const close = async () => {

			Events.off('route:changed', close);

			const element = document.getElementById(currentId);
			if (element !== null) {
				($(`#${currentId}`) as any).modal('hide');

				// Aparentemente, es necesario hacer el unmount de forma asíncrona
				// https://github.com/facebook/react/issues/25675
				setTimeout(() => root.unmount());
				element.remove();
			}

			dialogCount--;

			void Events.raise('pos-screen:closed');

			focusTrap.deactivate();
		};

		// Necesito cerrar el diálogo si se navega a otra ruta, por ejemplo porque el
		// usuario pulse el botón Back del browser
		Events.on('route:changed', close);


		const actions = new DialogActions<TResult>(resolve, close);

		if (!ScreenComponent) {
			throw new Error('Se esperaba un ScreenComponent != null. Lo mantenemos nullable en la signatura porque <Comp/> tiene tipo React.ReactNode | null');
		}

		const Component = React.cloneElement(ScreenComponent, { actions: actions } as any);

		const BackdropMounter = () => {
			React.useEffect(() => mountBackdrop());
			return Component;
		};

		root.render(<BackdropMounter />);
	});
}

export function showCustom<TResult>(DialogComponent: Nullable<React.ReactElement<DialogComponentProps<TResult>>>) {

	// No me atrevo a cambiar el código para no usar async dentro de la promesa. El código del diálogo es
	// un infierno y está "batle-tested", así que lo dejo como está.
	// eslint-disable-next-line no-async-promise-executor
	return new Promise<Result<TResult>>(async (resolve, _reject) => {

		// Dejo que se ejecuten antes otros timers. Esto lo hago por culpa
		// del focustrap. Si no, cuando sacas dos diálogos seguidos, sin hacer
		// ninguna llamada asíncrona entre ellos, se pierde el foco porque el
		// trap de App.tsx hace cosas raras. Se supone que con el
		// delayInitialFocus: false debería evitarse, pero no funciona.
		await new Promise(resolve => setTimeout(resolve, 0));

		dialogCount++;

		// Necesito hueco entre diálogos, al menos dos (uno para el diálogo y otro para su backdrop) pero por si acaso, dejo más.
		zIndex += 20;

		const currentId = `dialog-${zIndex}`;
		$('body').append(`<div id="${currentId}"></div>`);

		// Una vez agregado el contenedor principal del díalogo creo el objeto trampa
		// para bloquear el foco en el diálogo cuando esté montado
		const focusTrap = createFocusTrap(`#${currentId}`, { fallbackFocus: `#${currentId}`, escapeDeactivates: false, allowOutsideClick: true, clickOutsideDeactivates: false });

		const mountBackdrop = () => {

			focusTrap.activate();

			$(`#${currentId} .modal`).modal({
				show: true,
				keyboard: false,
				// Hacer que se cierre pinchando el backdrop da muchos problemas.
				// Muerto el perro, se acabó la rabia
				backdrop: 'static'
			});
			// Muevo el último backdrop justo detrás de mi (zIndex - 1)
			$('.modal-backdrop').last().css('z-index', zIndex - 1);

			void Events.raise('dialog:opened');

			sendFocusToInitialElement(document.getElementById(currentId)!);
		};

		const root = ReactDOMClient.createRoot(document.getElementById(currentId)!);

		const close = async () => {

			Events.off('route:changed', close);
			Events.off('browser:back', onBrowserBack);

			const element = document.getElementById(currentId);
			if (element !== null) {
				($(`#${currentId} .modal`) as any).modal('hide');

				// Aparentemente, es necesario hacer el unmount de forma asíncrona
				// https://github.com/facebook/react/issues/25675
				setTimeout(() => root.unmount());
				element.remove();
			}

			dialogCount--;

			// Bootstrap quita la clase modal-open del body cuando se cierra un
			// diálogo, pero necesito ponerla si todavía queda algún diálogo abierto,
			// o deja de funcionar el scroll
			if (dialogCount)
				$('body').addClass('modal-open');

			void Events.raise('dialog:closed');
			// Desactivo la trampa para liberar el foco y que la aplicación retome el control
			// a partir de aquí
			focusTrap.deactivate();
		};

		// Necesito cerrar el diálogo si se navega a otra ruta, por ejemplo porque el
		// usuario pulse el botón Back del browser
		Events.on('route:changed', close);

		const actions = new DialogActions<TResult>(resolve, close);

		const onBrowserBack = async ({ viewId }: { viewId: string }) => {
			if (viewId === currentId)
				actions.cancel();
		};

		Events.on('browser:back', onBrowserBack);

		if (!DialogComponent) {
			throw new Error('Se esperaba un DialogComponent != null. Lo mantenemos nullable en la signatura porque <Comp/> tiene tipo React.ReactNode | null');
		}

		const Component = React.cloneElement(DialogComponent, { actions: actions } as any);

		const BackdropMounter = () => {
			React.useEffect(() => mountBackdrop());
			return Component;
		};

		root.render(<BackdropMounter />);
	});
}

// Muestra un mensaje con un botón de aceptar
export function showMessage(message: string, title = '') {

	const MessageBox = (props: { title: string, message: string, actions?: DialogActions<boolean> }) => {

		const onClose = () => props.actions && props.actions.closeWith(true);

		const onKeyDown = (evt: React.KeyboardEvent<any>) => {
			if (evt.keyCode === 13 || evt.keyCode === 27) {
				evt.preventDefault();
				onClose();
			}
		};

		const buttonStyle = {
			minWidth: '80px'
		};

		return <Dialog onKeyDown={onKeyDown}>
			<DialogHeader>
				<button type='button' className='close' onClick={onClose}>&times; </button>
				<h4 className='modal-title'>{props.title || 'Atención'}</h4>
			</DialogHeader>
			<DialogBody>
				<p>{addLineBreaks(props.message)}</p>
			</DialogBody>
			<DialogFooter>
				<button type='button' className='btn btn-info icon ok auto-focus' onClick={onClose} style={buttonStyle}>Aceptar</button>
			</DialogFooter>
		</Dialog>;
	};

	Log.debug(`messageBox["${message}"].show()`);
	return showCustom(<MessageBox message={message} title={title} />);
}

// Muestra un mensaje de confirmación con botones de Sí o No.
// OJO: La promesa *siempre* se resuelve a Result.ok/Result.error, no se rechaza
//      cuando el usuario pulsa No
export function showConfirmation(message: string, title = ''): Promise<Result> {

	const ConfirmBox = (props: { title: string, message: string, actions?: DialogActions<void> }) => {

		const onYes = () => {
			Log.debug(`confirmBox["${message}"].close() -> Yes`);
			props.actions?.closeWith();
		};

		const onNo = () => {
			Log.debug(`confirmBox["${message}"].close() -> No`);
			props.actions?.cancel();
		};

		const onKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
			if (evt.keyCode === 13) {
				evt.preventDefault();
				onYes();
			}
			if (evt.keyCode === 27) {
				evt.preventDefault();
				onNo();
			}
		};

		const buttonStyle = {
			minWidth: '80px'
		};

		return <Dialog onKeyDown={onKeyDown}>
			<DialogHeader>
				<button type='button' className='close' onClick={onNo}>&times; </button>
				<h4 className='modal-title'>{props.title || 'Atención'}</h4>
			</DialogHeader>
			<DialogBody>
				<p>{addLineBreaks(props.message)}</p>
			</DialogBody>
			<DialogFooter>
				<button type='button' className='btn btn-info icon ok auto-focus' onClick={onYes} style={buttonStyle}>Sí</button>
				<button type='button' className='btn btn-default icon cancel' onClick={onNo} style={buttonStyle}>No</button>
			</DialogFooter>
		</Dialog>;
	};

	Log.debug(`confirmBox["${message}"].show()`);
	return showCustom<Result>(<ConfirmBox message={message} title={title} />);
}

export function ifConfirm(message: string, onConfirmed: () => void): Promise<void> {
	return showConfirmation(message).then(result => {
		if (result.ok)
			onConfirmed();
	});
}
